import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEventName } from '@/api/analytics';
import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { isIos } from '@/helpers/common';
import { useAnalytics } from '@/hooks/analytics';
import useEventListener from '@/hooks/eventListener';
import { useNavigation } from '@/hooks/navigation';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';

import { ScMobileAppPopup } from './styled';

export const MobileAppPopup = () => {
  const { t } = useTranslation('mobileApp');
  const { navigate } = useNavigation();
  const { sendEventToAnalytics } = useAnalytics();
  const ref = useRef<HTMLDivElement | null>(null);

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const isMobileAppPopupVisible = useCommonStore.use.isMobileAppPopupVisible();

  const lastSeenMobileAppPopup = useLocalStorageStore
    .getState()
    .get(STORAGE.MOBILE_APP_POPUP_LAST_SEEN_DATE);

  const handleCloseMobileAppPopup = useCallback(() => {
    useLocalStorageStore
      .getState()
      .replace(STORAGE.MOBILE_APP_POPUP_LAST_SEEN_DATE, Date.now());

    useCommonStore.setState({ isMobileAppPopupVisible: false });

    sendEventToAnalytics({
      name: AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__BUTTON_CLOSE__CLICK,
    });
  }, [sendEventToAnalytics]);

  const handleMobileAppLinkClick = useCallback(() => {
    let name: AnalyticsEventName;

    if (isMobileDevice) {
      if (isIos()) {
        name =
          AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__IOS_APP__LINK__CLICK;
      } else {
        name =
          AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__ANDROID_APP__LINK__CLICK;
      }
    } else {
      name =
        AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__MOBILE_APP__LINK__CLICK;
    }

    navigate('/mobile-app');
    sendEventToAnalytics({ name });
  }, [isMobileDevice, navigate, sendEventToAnalytics]);

  const mobileAppPopupApperance = useCallback(() => {
    if (isMobileAppPopupVisible) {
      sendEventToAnalytics({
        name:
          lastSeenMobileAppPopup === null
            ? AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__FIRST_APPEARANCE
            : AnalyticsEventName.MOBILE_APP_PROMOTION_POPUP__SUBSEQUENT_APPEARANCE,
      });
    }
  }, [isMobileAppPopupVisible, lastSeenMobileAppPopup, sendEventToAnalytics]);

  useEffect(() => {
    mobileAppPopupApperance();
  }, [lastSeenMobileAppPopup, mobileAppPopupApperance, sendEventToAnalytics]);

  useEventListener(
    'resize',
    () =>
      ref.current &&
      useCommonStore.setState({
        mobileAppPopupHeight: ref.current.clientHeight,
      }),
    undefined,
    true
  );

  return (
    <ScMobileAppPopup ref={ref}>
      <div className="wrapper">
        <ImageComponent
          src="/promoMobileApp.webp"
          width={isMobileDevice ? 40 : 32}
          height={isMobileDevice ? 40 : 32}
          alt="promoMobileApp"
        />
        <div className="text">
          <span className="title">{t('popup.title')}</span>
          <span>{t('popup.subTitle')}</span>
        </div>
        <div className="wrapper-button">
          <Button
            autoPaddingHorizontal
            className="button"
            primary
            height="32px"
            onClick={handleMobileAppLinkClick}
          >
            {t('button.install')}
          </Button>
        </div>
        <div className="wrapper-button-close">
          <ButtonClose
            gray
            small
            onClick={handleCloseMobileAppPopup}
            className="button-close"
          />
        </div>
      </div>
    </ScMobileAppPopup>
  );
};
